import { supabase } from "@/integrations/supabase/client";
import {
  formatCashMovementReceipt,
  formatCashRegisterCloseReceipt,
  ensureQZConnected,
  getPrinterSettings,
} from "@/lib/qz-tray";
import { getPrinterNameForJob } from "@/lib/print-routing";
import { fetchPrintTemplate } from "@/hooks/usePrintTemplates";
import { dispatchPrint } from "@/lib/print-dispatcher";

interface PrintCashMovementParams {
  type: "withdrawal" | "supply";
  amount: number;
  reason?: string;
  restaurantId: string;
  printerName?: string;
}

export async function printCashMovement(params: PrintCashMovementParams): Promise<boolean> {
  // Ensure QZ Tray is connected (with retry)
  const connected = await ensureQZConnected();
  if (!connected) {
    console.warn("[PRINT] QZ Tray não conectado - impressão automática ignorada");
    return false;
  }

  try {
    // Get restaurant info
    const { data: restaurant } = await supabase
      .from("restaurants")
      .select("name")
      .eq("id", params.restaurantId)
      .single();

    // Get current user profile
    const { data: { user } } = await supabase.auth.getUser();
    let operatorName: string | undefined;
    
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();
      operatorName = profile?.full_name || undefined;
    }

    // Get printer name - use provided or find from routing
    let printerName = params.printerName;
    if (!printerName) {
      const jobType = params.type === "withdrawal" ? "cash_withdrawal" : "cash_supply";
      printerName = await getPrinterNameForJob(params.restaurantId, jobType);
    }

    if (!printerName) {
      return false;
    }

    // Get printer settings for chars_per_line
    const printerSettings = await getPrinterSettings(printerName, params.restaurantId);
    const charsPerLine = printerSettings?.chars_per_line || 48;

    // Fetch print template for cash movement
    const template = await fetchPrintTemplate(params.restaurantId, "cash_movement");

    const content = formatCashMovementReceipt({
      type: params.type,
      amount: params.amount,
      reason: params.reason,
      operatorName,
      restaurantName: restaurant?.name,
    }, charsPerLine, template);

    return await dispatchPrint(params.restaurantId, printerName, content, {
      logType: "caixa",
      logIdentifier: params.type === "withdrawal" ? "sangria" : "suprimento",
      settings: printerSettings || undefined,
    });
  } catch (error) {
    console.error("Error printing cash movement:", error);
    return false;
  }
}

interface PrintCashRegisterCloseParams {
  cashRegisterId: string;
  restaurantId: string;
  openedAt: string;
  closedAt: string;
  openingAmount: number;
  supplies: number;
  withdrawals: number;
  cashSales: number;
  expectedAmount: number;
  countedAmount: number;
  difference: number;
  notes?: string;
  printerName?: string;
}

export async function printCashRegisterClose(params: PrintCashRegisterCloseParams): Promise<boolean> {
  const connected = await ensureQZConnected();
  if (!connected) {
    console.warn("[PRINT] QZ Tray não conectado - fechamento de caixa não impresso");
    return false;
  }

  try {
    // Get restaurant info
    const { data: restaurant } = await supabase
      .from("restaurants")
      .select("name")
      .eq("id", params.restaurantId)
      .single();

    // Get current user profile
    const { data: { user } } = await supabase.auth.getUser();
    let operatorName: string | undefined;
    
    if (user) {
      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", user.id)
        .single();
      operatorName = profile?.full_name || undefined;
    }

    // Get cash count details if available
    const { data: cashCountDetails } = await supabase
      .from("cash_count_details")
      .select("denomination, quantity, total, type")
      .eq("cash_register_id", params.cashRegisterId);

    // === NEW: Fetch order totals for the period ===
    const { data: periodOrders } = await supabase
      .from("orders")
      .select("subtotal, discount, additional_fees, couvert_amount, total, order_type, external_source, status")
      .eq("restaurant_id", params.restaurantId)
      .in("status", ["paid", "delivered"])
      .gte("created_at", params.openedAt)
      .lte("created_at", params.closedAt);

    let subtotalProducts = 0;
    let totalDiscount = 0;
    let totalServiceTax = 0;
    let totalCouvert = 0;
    let totalRevenue = 0;

    if (periodOrders) {
      for (const order of periodOrders) {
        subtotalProducts += Number(order.subtotal) || 0;
        totalDiscount += Number(order.discount) || 0;
        totalServiceTax += Number(order.additional_fees) || 0;
        totalCouvert += Number(order.couvert_amount) || 0;
        totalRevenue += Number(order.total) || 0;
      }
    }

    // === Breakdown by order_type (with iFood sub-breakdown on delivery) ===
    const typeLabels: Record<string, string> = {
      table: "Mesa",
      tab: "Comanda",
      counter: "Balcao",
      delivery: "Delivery",
    };
    const typeAgg: Record<string, { total: number; count: number; sources: Record<string, { total: number; count: number }> }> = {
      table: { total: 0, count: 0, sources: {} },
      tab: { total: 0, count: 0, sources: {} },
      counter: { total: 0, count: 0, sources: {} },
      delivery: { total: 0, count: 0, sources: {} },
    };
    let ifoodDeliveryTotal = 0;
    if (periodOrders) {
      for (const o of periodOrders as any[]) {
        const key = (o.order_type as string) || "counter";
        if (!typeAgg[key]) continue;
        const tot = Number(o.total) || 0;
        typeAgg[key].total += tot;
        typeAgg[key].count += 1;
        if (key === "delivery") {
          const src = (o.external_source as string) || "manual";
          typeAgg[key].sources[src] = typeAgg[key].sources[src] || { total: 0, count: 0 };
          typeAgg[key].sources[src].total += tot;
          typeAgg[key].sources[src].count += 1;
          if (src === "ifood") ifoodDeliveryTotal += tot;
        }
      }
    }
    const salesByOrderType = (Object.keys(typeAgg) as Array<keyof typeof typeAgg>).map((k) => ({
      orderType: k as string,
      label: typeLabels[k],
      total: typeAgg[k].total,
      count: typeAgg[k].count,
      sources: k === "delivery"
        ? Object.entries(typeAgg[k].sources).map(([source, s]) => ({
            source,
            label: source === "ifood" ? "iFood" : source === "manual" ? "Manual" : source,
            total: s.total,
            count: s.count,
          }))
        : undefined,
    }));

    // === Fetch sales by payment method (from delivered orders only) ===
    const { data: orderPayments } = await supabase
      .from("order_payments")
      .select(`
        amount,
        order_id,
        payment_methods!inner(name),
        orders!inner(restaurant_id, status)
      `)
      .eq("orders.restaurant_id", params.restaurantId)
      .in("orders.status", ["paid", "delivered"])
      .gte("created_at", params.openedAt)
      .lte("created_at", params.closedAt);

    // Fetch partial payments ONLY from orders that are NOT yet paid/delivered
    // (avoids double counting - once order closes, partial_payments are already in order_payments)
    const { data: partialPayments } = await supabase
      .from("partial_payments")
      .select(`
        amount,
        payment_methods!inner(name),
        orders!inner(status)
      `)
      .eq("restaurant_id", params.restaurantId)
      .not("orders.status", "in", "(paid,delivered)")
      .gte("created_at", params.openedAt)
      .lte("created_at", params.closedAt);

    // Aggregate by payment method
    const paymentTotals = new Map<string, number>();
    
    if (orderPayments) {
      for (const op of orderPayments) {
        const methodName = (op.payment_methods as any)?.name || "Outros";
        const current = paymentTotals.get(methodName) || 0;
        paymentTotals.set(methodName, current + Number(op.amount));
      }
    }
    
    // Add partial payments from open orders only
    if (partialPayments) {
      for (const pp of partialPayments) {
        const methodName = (pp.payment_methods as any)?.name || "Outros";
        const current = paymentTotals.get(methodName) || 0;
        paymentTotals.set(methodName, current + Number(pp.amount));
      }
    }

    const salesByPaymentMethod = Array.from(paymentTotals.entries())
      .map(([name, total]) => ({ name, total }))
      .sort((a, b) => b.total - a.total);

    // Synthetic line: iFood delivery is paid on the platform, never hits order_payments
    if (ifoodDeliveryTotal > 0) {
      salesByPaymentMethod.push({ name: "Delivery iFood", total: ifoodDeliveryTotal });
    }

    // === NEW: Fetch cancellations for the period ===
    const { data: cancellationsData } = await supabase
      .from("order_item_cancellations")
      .select(`
        product_name,
        quantity,
        total_price,
        reason,
        cancelled_by
      `)
      .eq("restaurant_id", params.restaurantId)
      .gte("cancelled_at", params.openedAt)
      .lte("cancelled_at", params.closedAt);

    // Fetch profiles for cancelled_by users
    const cancellerIds = [...new Set(cancellationsData?.map(c => c.cancelled_by) || [])];
    const { data: cancellerProfiles } = cancellerIds.length > 0 
      ? await supabase
          .from("profiles")
          .select("id, full_name")
          .in("id", cancellerIds)
      : { data: [] };

    const profileMap = new Map<string, string>();
    if (cancellerProfiles) {
      for (const p of cancellerProfiles) {
        profileMap.set(p.id, p.full_name || "Desconhecido");
      }
    }

    const cancellations = cancellationsData?.map(c => ({
      productName: c.product_name,
      quantity: c.quantity,
      totalValue: Number(c.total_price),
      cancelledBy: profileMap.get(c.cancelled_by) || "Desconhecido",
      reason: c.reason || undefined,
    })) || [];

    const totalCancellations = cancellations.reduce((sum, c) => sum + c.totalValue, 0);

    // Get printer name from routing
    let printerName = params.printerName;
    if (!printerName) {
      printerName = await getPrinterNameForJob(params.restaurantId, "cash_register_close");
    }

    if (!printerName) {
      return false;
    }

    // Get printer settings for chars_per_line
    const printerSettings = await getPrinterSettings(printerName, params.restaurantId);
    const charsPerLine = printerSettings?.chars_per_line || 48;

    const content = formatCashRegisterCloseReceipt({
      restaurantName: restaurant?.name,
      openedAt: params.openedAt,
      closedAt: params.closedAt,
      operatorName,
      openingAmount: params.openingAmount,
      supplies: params.supplies,
      withdrawals: params.withdrawals,
      cashSales: params.cashSales,
      expectedAmount: params.expectedAmount,
      countedAmount: params.countedAmount,
      difference: params.difference,
      notes: params.notes,
      cashCountDetails: cashCountDetails?.map(d => ({
        denomination: Number(d.denomination),
        quantity: d.quantity,
        total: Number(d.total),
        type: d.type as "note" | "coin",
      })),
      // New fields
      totalRevenue,
      subtotalProducts,
      totalDiscount,
      totalServiceTax,
      totalCouvert,
      salesByPaymentMethod,
      cancellations,
      totalCancellations,
      salesByOrderType,
    }, charsPerLine);

    return await dispatchPrint(params.restaurantId, printerName, content, {
      logType: "caixa",
      logIdentifier: "fechamento",
      settings: printerSettings || undefined,
    });
  } catch (error) {
    console.error("Error printing cash register close:", error);
    return false;
  }
}
